import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function preventBodyScroll(preventScroll: boolean) {
  if (preventScroll) {
    document.body.classList.add("overflow-hidden")
  } else {
    document.body.classList.remove("overflow-hidden")
  }
}

export const safeJSONParse = (string: string): unknown => {
  try {
    return JSON.parse(string)
  } catch (err) {
    return {
      details: string,
    }
  }
}

export function maskAllExceptLast2(
  code: number | string | null | undefined,
): string {
  if (code == null) return ""
  const stringCode = String(code)
  const masked = stringCode.slice(0, -2).replaceAll(/./g, "*")
  const visible = stringCode.slice(-2)
  return masked + visible
}

interface CurrencyFormatOptions {
  hideDecimals?: boolean
  negate?: boolean
}

export const formatCurrency = (
  amount: number,
  options: CurrencyFormatOptions = {},
) => {
  amount = options.negate && amount !== 0 ? -amount : amount

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: options.hideDecimals ? 0 : 2,
    maximumFractionDigits: options.hideDecimals ? 0 : 2,
  }).format(amount)
}

export const safeFormatCurrency = (
  amount?: number,
  options: CurrencyFormatOptions = {},
) => (amount === undefined ? "--.--" : formatCurrency(amount, options))

export const formatCurrencyInPence = (
  amountInPence: number,
  fractionDigits = 2,
) => {
  const penceSymbol = "p"
  const amount = new Intl.NumberFormat("en-GB", {
    style: "decimal",
    maximumFractionDigits: fractionDigits,
  }).format(amountInPence)

  return `${amount}${penceSymbol}`
}

/**
 * Formats a decimal fraction as an en-GB percentage, e.g. 0.05 -> "5%".
 * Returns undefined for a missing value so callers can hide it rather than
 * render a misleading "0%".
 */
export const formatPercentage = (
  fraction?: number | null,
  maximumFractionDigits = 2,
) =>
  fraction == null
    ? undefined
    : new Intl.NumberFormat("en-GB", {
        style: "percent",
        maximumFractionDigits,
      }).format(fraction)

export const removeLeadingSlash = (str?: string) =>
  str?.replace(/^\/+/, "") ?? ""
export const ensureLeadingSlash = (str: string) => str.replace(/^\/?/, "/")
export const removeTrailingSlash = (str: string) => str.replace(/\/$/, "")
export const ensureTrailingSlash = (str: string) => str.replace(/\/?$/, "/")

export const getBalanceStatus = (balance: number) =>
  balance < 0 ? "in credit" : "in debit"

/**
 * Groups an array of items by a key derived from each item.
 * Replicates https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/groupBy
 * @param items - The array of items to group.
 * @param keyFn - A function that returns the grouping key for each item. If undefined is returned, the item is grouped under an empty string.
 * @returns A record where keys are the group identifiers and values are arrays of items belonging to each group.
 */
export function groupBy<T>(
  items: T[],
  keyFn: (item: T) => string | undefined,
): Record<string, T[]> {
  return items.reduce<Record<string, T[]>>((acc, item) => {
    const key = keyFn(item) ?? ""
    if (!acc[key]) acc[key] = []
    acc[key].push(item)
    return acc
  }, {})
}
